import { useState, useEffect, useCallback } from 'react'

const STORAGE_KEY_PREFIX = 'gsu_welcome_popup_dismissed_'

/**
 * Hook for managing partner welcome popup visibility and dismiss state.
 *
 * Tracks popup dismissal per partner using localStorage. The popup shows
 * with a 500ms delay after mount to ensure the page has rendered.
 *
 * @param partnerId - The partner ID to track dismiss state for
 * @returns Object with shouldShow boolean and dismiss function
 */
export function useWelcomePopup(partnerId: string | undefined): { shouldShow: boolean; dismiss: () => void } {
  const [shouldShow, setShouldShow] = useState(false)

  const storageKey = partnerId ? `${STORAGE_KEY_PREFIX}${partnerId}` : null

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!storageKey) return

    const dismissed = localStorage.getItem(storageKey) === 'true'

    if (!dismissed) {
      const timer = setTimeout(() => setShouldShow(true), 500)
      return () => clearTimeout(timer)
    }
  }, [storageKey])

  const dismiss = useCallback(() => {
    if (storageKey && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, 'true')
    }
    setShouldShow(false)
  }, [storageKey])

  return { shouldShow, dismiss }
}
